// OIDC-mode UI hints that point users to the GoFranz SSO portal via the
// `/handoff` deep-link. Used in profile + security pages for OIDC users; for
// other login methods these components return `null` and the existing local
// UI keeps rendering.

import { Anchor, Button, Group, Notification, Stack, Text } from '@mantine/core';
import { IconExternalLink, IconRefresh, IconShieldCheck } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { buildIdpHandoffUrl, type OidcHandoffConfig } from '@/api';

export interface IdpHandoffButtonProps {
  config: OidcHandoffConfig;
  action: string;
  returnTo: string;
  label: ReactNode;
  /** Mantine button variant. Defaults to "light". */
  variant?: string;
}

/** A button that opens the IdP's `/handoff?action=...` page in the same tab,
 *  so the portal's banner + return-to flow works. Returns `null` if the action
 *  verb isn't in the portal's supported list (caller can fall back to the
 *  account-management URI or hide the button entirely). */
function IdpHandoffButton({ config, action, returnTo, label, variant }: IdpHandoffButtonProps) {
  const url = buildIdpHandoffUrl({ config, action, returnTo });
  if (!url) return null;
  return (
    <Button
      component="a"
      href={url}
      variant={variant ?? 'light'}
      rightSection={<IconExternalLink size={16} />}
    >
      {label}
    </Button>
  );
}

export interface OidcTwoFactorRedirectProps {
  /** Handoff config from discovery. `null` when portal discovery didn't load
   *  — the explanatory copy still renders, the deep-link button is hidden. */
  config: OidcHandoffConfig | null;
  returnTo: string;
}

/** Drop-in replacement for the local TwoFactorSettings component when the
 *  session was established via OIDC. The headline stays the same in the
 *  parent card; this just renders the body. */
export function OidcTwoFactorRedirect({ config, returnTo }: OidcTwoFactorRedirectProps) {
  const { t } = useTranslation();
  return (
    <Stack gap="sm">
      <Text size="sm" c="dimmed">
        {t('oidc.handoffTwoFactorBody')}
      </Text>
      {config && (
        <Group>
          <IdpHandoffButton
            config={config}
            action="2fa"
            returnTo={returnTo}
            label={t('oidc.handoffTwoFactorButton')}
          />
        </Group>
      )}
    </Stack>
  );
}

export interface OidcSecurityLogBannerProps {
  /** Handoff config from discovery. `null` when portal discovery didn't load
   *  — the banner still renders the explanatory copy but without a link. */
  config: OidcHandoffConfig | null;
}

/** Banner shown above the security log for OIDC users. Points at the IdP's
 *  account-management URI (which is closer to "all your auth events" than
 *  the `sessions` deep-link, and is always present in the discovery doc). */
export function OidcSecurityLogBanner({ config }: OidcSecurityLogBannerProps) {
  const { t } = useTranslation();
  return (
    <Notification
      withCloseButton={false}
      withBorder
      color="gray"
      icon={<IconShieldCheck size={18} />}
      title={t('oidc.securityLogBanner')}
      mb="md"
    >
      {config && (
        <Anchor href={config.account_management_uri} size="sm" mt="xs">
          {t('oidc.securityLogLink')}
          <IconExternalLink size={14} style={{ verticalAlign: 'middle', marginLeft: 4 }} />
        </Anchor>
      )}
    </Notification>
  );
}

export interface OidcSyncFromIdpButtonProps {
  /** Called when the user clicks "Sync from IdP". Implementer should trigger
   *  the silent OIDC re-auth (POST /v1/login OIDC with `return_to` set to
   *  the current account-page URL) and then perform the browser redirect to
   *  the returned auth_url. */
  onSync: () => Promise<void> | void;
  busy?: boolean;
}

/** Manual fallback for the refresh-on-return flow. Useful when the user
 *  changed something on the IdP without going through our handoff link. */
export function OidcSyncFromIdpButton({ onSync, busy }: OidcSyncFromIdpButtonProps) {
  const { t } = useTranslation();
  return (
    <Button
      variant="subtle"
      size="xs"
      loading={busy}
      leftSection={<IconRefresh size={14} />}
      onClick={() => {
        void onSync();
      }}
    >
      {busy ? t('oidc.syncFromIdpInProgress') : t('oidc.syncFromIdpButton')}
    </Button>
  );
}
